import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';
import { getUserDisplayName } from '../utils/userMapping';

function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    if (user?.email) {
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const notificationsRef = collection(db, 'user_notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', user.email),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const notificationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setNotifications(notificationsList);
    } catch (error) {
      console.error('알림 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'user_notifications', notificationId);
      await updateDoc(notificationRef, { read: true });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('알림 읽음 처리 오류:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      const unreadNotifications = notifications.filter(n => !n.read);

      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, 'user_notifications', notification.id);
        batch.update(notificationRef, { read: true });
      });

      await batch.commit();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('모든 알림 읽음 처리 오류:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'user_notifications', notificationId));
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('알림 삭제 오류:', error);
    }
  };

  const deleteAllRead = async () => {
    if (!window.confirm('읽은 알림을 모두 삭제하시겠습니까?')) return;

    try {
      const batch = writeBatch(db);
      const readNotifications = notifications.filter(n => n.read);

      readNotifications.forEach(notification => {
        const notificationRef = doc(db, 'user_notifications', notification.id);
        batch.delete(notificationRef);
      });

      await batch.commit();
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (error) {
      console.error('읽은 알림 삭제 오류:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.productId) {
      navigate(`/inventory/${notification.productId}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'location_change':
        return { icon: '📦', bg: 'bg-blue-100', text: 'text-blue-600' };
      case 'quantity_change':
        return { icon: '📊', bg: 'bg-purple-100', text: 'text-purple-600' };
      case 'sale':
        return { icon: '💰', bg: 'bg-green-100', text: 'text-green-600' };
      case 'location_zone_mismatch':
        return { icon: '⚠️', bg: 'bg-yellow-100', text: 'text-yellow-600' };
      default:
        return { icon: '🔔', bg: 'bg-gray-100', text: 'text-gray-600' };
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return '방금 전';
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;

    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-brand-900">알림</h1>
          {unreadCount > 0 && (
            <span className="px-2.5 py-1 text-sm font-semibold text-white bg-red-500 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50 rounded-lg transition"
            >
              모두 읽음
            </button>
          )}
          {notifications.some(n => n.read) && (
            <button
              onClick={deleteAllRead}
              className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition"
            >
              읽은 알림 삭제
            </button>
          )}
        </div>
      </div>

      {/* 필터 탭 */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {[
          { key: 'all', label: '전체', count: notifications.length },
          { key: 'unread', label: '읽지 않음', count: unreadCount },
          { key: 'read', label: '읽음', count: notifications.length - unreadCount }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              filter === tab.key
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* 알림 목록 */}
      {filteredNotifications.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Icons.Bell className="w-10 h-10 text-gray-400" />
          </div>
          <p className="text-gray-500">
            {filter === 'unread' ? '읽지 않은 알림이 없습니다' :
             filter === 'read' ? '읽은 알림이 없습니다' :
             '알림이 없습니다'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => {
            const iconStyle = getNotificationIcon(notification.type);
            return (
              <div
                key={notification.id}
                className={`bg-white rounded-lg border shadow-sm overflow-hidden transition hover:shadow-md ${
                  notification.read ? 'border-gray-200' : 'border-brand-300 bg-brand-50/30'
                }`}
              >
                <div
                  onClick={() => handleNotificationClick(notification)}
                  className="p-4 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    {/* 아이콘 */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconStyle.bg}`}>
                      <span className="text-2xl">{iconStyle.icon}</span>
                    </div>

                    {/* 내용 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`font-semibold ${notification.read ? 'text-gray-700' : 'text-brand-900'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-brand-600 rounded-full"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>{formatDate(notification.createdAt)}</span>
                        {notification.triggeredBy && (
                          <>
                            <span>•</span>
                            <span className="text-gray-500">
                              수정자: <span className="font-medium text-brand-600">{getUserDisplayName(notification.triggeredBy)}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* 삭제 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    >
                      <Icons.X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Notifications;
